import type { PageInfo } from 'iframe-resizer';

declare global {
	interface Window {
		iFrameResizer?: {
			heightCalculationMethod?: string;
			onReady?: () => void;
		};
		parentIFrame?: {
			getId?: () => string;
			getPageInfo?: {
				(callback: (info: PageInfo) => void): void;
				(stop: false): void;
			};
			setHeightCalculationMethod?: (method: string) => void;
			setTargetOrigin?: (referrerOrigin: string) => void;
			size?: () => void;
		};
	}
}

const IFRAME_RESIZER_ID = 'iFrameResizer1';
const IFRAME_RESIZER_TAGGED_ELEMENT_ID = 'root';
const LOG_PREFIX = '[ClinicalReality iFrameResizer]';

const log = (message: string, data?: unknown) => {
	if (data !== undefined) {
		console.log(LOG_PREFIX, message, data);
		return;
	}

	console.log(LOG_PREFIX, message);
};

export const ensureIframeResizeTaggedElement = () => {
	const taggedElement = document.getElementById(IFRAME_RESIZER_TAGGED_ELEMENT_ID);

	if (!taggedElement) {
		log('tagged element not found', { taggedElementId: IFRAME_RESIZER_TAGGED_ELEMENT_ID });
		return null;
	}

	if (!taggedElement.hasAttribute('data-iframe-height')) {
		taggedElement.setAttribute('data-iframe-height', '');
		log('data-iframe-height attribute added to root element');
	}

	return taggedElement;
};

export const setIframeResizeTaggedElementHeight = (height: string) => {
	const taggedElement = ensureIframeResizeTaggedElement();

	if (!taggedElement) {
		return;
	}

	if (height === 'auto') {
		taggedElement.style.height = '';
		taggedElement.style.minHeight = '';
	} else {
		taggedElement.style.height = height;
		taggedElement.style.minHeight = height;
	}

	log('tagged element height updated', {
		taggedElementId: IFRAME_RESIZER_TAGGED_ELEMENT_ID,
		height,
	});
};

if (typeof window !== 'undefined') {
	const existingOnReady = window.iFrameResizer?.onReady;

	ensureIframeResizeTaggedElement();

	log('loading contentWindow bridge', {
		expectedIframeId: IFRAME_RESIZER_ID,
		heightCalculationMethod: 'taggedElement',
	});

	window.addEventListener('message', (event) => {
		if (typeof event.data === 'string' && event.data.indexOf('[iFrameSizer]') === 0) {
			log('received parent message', event.data);
		}
	});

	window.iFrameResizer = {
		...window.iFrameResizer,
		heightCalculationMethod: 'taggedElement',
		onReady: () => {
			existingOnReady?.();

			const parentIFrame = window.parentIFrame;
			const iframeId = parentIFrame?.getId?.();
			const isExpectedIframe = iframeId === IFRAME_RESIZER_ID;

			log('connected to parent', {
				iframeId,
				isExpectedIframe,
				hasParentIFrame: Boolean(parentIFrame),
			});

			if (isExpectedIframe) {
				parentIFrame?.setHeightCalculationMethod?.('taggedElement');
				parentIFrame?.size?.();
				log('taggedElement height calculation enabled');
			}
		},
	};

	window.setTimeout(() => {
		if (!window.parentIFrame) {
			log('parentIFrame is still unavailable after 5 seconds');
		}
	}, 5000);
}

require('iframe-resizer/js/iframeResizer.contentWindow');

export {};
